import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, Check, Loader2 } from "lucide-react";
import { Calendar } from "./ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { TOURS } from "./Tours";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    tour_id: TOURS[0].id,
    guests: 2,
    message: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const id = e.detail;
      if (TOURS.find((tt) => tt.id === id)) {
        setForm((f) => ({ ...f, tour_id: id }));
      }
    };
    window.addEventListener("prefill-tour", handler);
    return () => window.removeEventListener("prefill-tour", handler);
  }, []);

  const selectedTour = TOURS.find((tt) => tt.id === form.tour_id) || TOURS[0];

  const handle = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error(t("booking.err_no_date"));
      return;
    }
    if (!form.name || !form.email || !form.phone) {
      toast.error(t("booking.err_required"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        tour_id: form.tour_id,
        tour_name: t(selectedTour.nameKey),
        date: date.toISOString().slice(0, 10),
        guests: Number(form.guests),
        message: form.message || "",
      };
      await axios.post(`${API}/bookings`, payload);
      setSuccess(true);
      toast.success(t("booking.toast_success"));
      setForm({
        name: "",
        email: "",
        phone: "",
        tour_id: TOURS[0].id,
        guests: 2,
        message: "",
      });
      setDate(null);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        t("booking.err_generic");
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="relative py-24 lg:py-36 bg-[#FAF7F2]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-[#0A1B35]" />
            <span className="overline text-[#0A1B35]">{t("booking.eyebrow")}</span>
          </div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#0A1B35] leading-[1.05]">
            {t("booking.title_pre")} <em className="italic">{t("booking.title_em")}</em>
            {t("booking.title_dot")}
          </h2>
          <p className="mt-8 text-[#4B5563] font-light leading-relaxed max-w-md">
            {t("booking.body")}
          </p>

          <div className="mt-12 border-t border-[#0A1B35]/10 pt-8 space-y-5 text-sm text-[#4B5563]">
            <div>
              <div className="overline text-[#0A1B35] mb-1">{t("booking.port_label")}</div>
              <div>{t("booking.port_value")}</div>
            </div>
            <div>
              <div className="overline text-[#0A1B35] mb-1">{t("booking.tel_label")}</div>
              <div>+389 71 326 613</div>
              <div>+389 70 231 783</div>
            </div>
            <div>
              <div className="overline text-[#0A1B35] mb-1">{t("booking.email_label")}</div>
              <a href="mailto:arsvivendisailingclub@gmail.com" className="hover:text-[#0A1B35]">arsvivendisailingclub@gmail.com</a>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          onSubmit={onSubmit}
          data-testid="booking-form"
          className="lg:col-span-7 bg-white border border-[#0A1B35]/10 p-6 sm:p-10 lg:p-12"
        >
          {success && (
            <div
              data-testid="booking-success"
              className="mb-8 flex items-start gap-3 bg-[#0A1B35] text-white p-5"
            >
              <Check className="w-5 h-5 mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="font-serif-display text-xl">{t("booking.success_title")}</div>
                <div className="text-white/80 text-sm mt-1">{t("booking.success_body")}</div>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <Field label={t("booking.f_name")}>
              <input
                data-testid="booking-name"
                type="text"
                required
                value={form.name}
                onChange={handle("name")}
                className="w-full bg-transparent border-b border-[#0A1B35]/20 focus:border-[#0A1B35] py-3 text-[#0A1B35] placeholder:text-[#0A1B35]/40 transition-colors"
                placeholder="John Doe"
              />
            </Field>
            <Field label={t("booking.f_email")}>
              <input
                data-testid="booking-email"
                type="email"
                required
                value={form.email}
                onChange={handle("email")}
                className="w-full bg-transparent border-b border-[#0A1B35]/20 focus:border-[#0A1B35] py-3 text-[#0A1B35] placeholder:text-[#0A1B35]/40 transition-colors"
                placeholder="you@example.com"
              />
            </Field>
            <Field label={t("booking.f_phone")}>
              <input
                data-testid="booking-phone"
                type="tel"
                required
                value={form.phone}
                onChange={handle("phone")}
                className="w-full bg-transparent border-b border-[#0A1B35]/20 focus:border-[#0A1B35] py-3 text-[#0A1B35] placeholder:text-[#0A1B35]/40 transition-colors"
                placeholder="+389 70 000 000"
              />
            </Field>

            <Field label={t("booking.f_guests")}>
              <input
                data-testid="booking-guests"
                type="number"
                min={1}
                max={5}
                value={form.guests}
                onChange={handle("guests")}
                className="w-full bg-transparent border-b border-[#0A1B35]/20 focus:border-[#0A1B35] py-3 text-[#0A1B35] transition-colors"
              />
            </Field>

            <Field label={t("booking.f_tour")}>
              <Select
                value={form.tour_id}
                onValueChange={(v) => setForm((f) => ({ ...f, tour_id: v }))}
              >
                <SelectTrigger
                  data-testid="booking-tour-trigger"
                  className="w-full bg-transparent border-0 border-b border-[#0A1B35]/20 rounded-none py-3 h-auto text-[#0A1B35] focus:ring-0 focus:border-[#0A1B35] px-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent data-testid="booking-tour-content">
                  {TOURS.map((tt) => (
                    <SelectItem key={tt.id} value={tt.id} data-testid={`booking-tour-${tt.id}`}>
                      {t(tt.nameKey)} · {t(tt.durationKey)}{tt.price ? ` · ${tt.price}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label={t("booking.f_date")}>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="booking-date-trigger"
                    className="w-full text-left bg-transparent border-b border-[#0A1B35]/20 hover:border-[#0A1B35] py-3 text-[#0A1B35] flex items-center justify-between transition-colors"
                  >
                    <span className={date ? "" : "text-[#0A1B35]/40"}>
                      {date ? date.toDateString() : t("booking.f_date_placeholder")}
                    </span>
                    <CalendarIcon className="w-4 h-4 opacity-60" strokeWidth={1.4} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto bg-white" align="start" data-testid="booking-date-popover">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>
          </div>

          <div className="mt-8">
            <Field label={t("booking.f_message")}>
              <textarea
                data-testid="booking-message"
                rows={3}
                value={form.message}
                onChange={handle("message")}
                className="w-full bg-transparent border-b border-[#0A1B35]/20 focus:border-[#0A1B35] py-3 text-[#0A1B35] placeholder:text-[#0A1B35]/40 transition-colors resize-none"
                placeholder={t("booking.f_message_placeholder")}
              />
            </Field>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div className="text-xs text-[#4B5563]">{t("booking.disclaimer")}</div>
            <button
              type="submit"
              data-testid="booking-submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> {t("booking.submitting")}
                </>
              ) : (
                <>{t("booking.submit")}</>
              )}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-[#0A1B35] block mb-1">{label}</span>
      {children}
    </label>
  );
}
